import React, { useRef, useState } from "react";
import { 
	RCCBadge, 
	RCCButton, 
	RCCGrid,  
	RCCTooltip, 
	RCCDropDownItem, 
	RCCIcon,
	RCCInput,
	RCCSwitch,
	RCCHookToast,
	RCCServicesApi
} from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import Pagination from "src/components/Pagination";
import PageTitle from "src/components/PageTitle";
import FiltersContainer from "src/components/FiltersContainer";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import formatDate from "src/utils/formatDate";
import Photo from "src/components/Photo";
import Accordion from "src/components/Accordion";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { standardErrorMessages } from "src/utils/errors";
import AppIcons from "src/components/AppIcons";
import ModalIndicatorsPermissions from "src/components/ModalIndicatorsPermissions";
import { IModalIndicatorsPermissionsRef } from "src/components/ModalIndicatorsPermissions/interface";
import { useAuth } from "src/context/Auth/context";
import ModalsProfile from "src/pages/Profile/modals";
import { IRefModalsProfile } from "src/pages/Profile/interface";
import DivFlexDirectionColumn from "./styles";
import { IRefUsersModals } from "./interface";
import ModalsUsers from "./modals";
import ModalChangeResponsibleOrAuthority from "./components/ModalChangeResponsibleOrAuthority";
import { IModalChangeResponsibleOrAuthorityRef } from "./components/ModalChangeResponsibleOrAuthority/interface";
import ModalImportUsers from "./components/ModalImportUsers";
import { IRefImportUsers } from "./components/ModalImportUsers/interface";

const UsersPage: React.FC = () => { 

	const { businessGroup } = useAuth();
	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;

	const refModals = useRef<IRefUsersModals>(null);
	const refModalImportUsers = useRef<IRefImportUsers>(null);
	const refModalsProfile = useRef<IRefModalsProfile>(null);

	const refModalIndicatorsPermissions = useRef<IModalIndicatorsPermissionsRef>(null);
	const refModalChangeResponsibleOrAuthority = useRef<IModalChangeResponsibleOrAuthorityRef>(null);

	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<{ f: string; enabledUsers: boolean; }>({ f: "", enabledUsers: true });
	const { handleSubmit : handleSubmitFilters, register : registerFilters, reset: resetFilters } = useForm();
	const submitFilters = (data: any) => {
		setFilters({ f: data.f, enabledUsers: data.enabledUsers });
		paginationRef.current?.reload();
	};
	
	// List & Pagination
	const paginationRef = useRef<IForwardPagination>(null);
	const colOptions = (row: any) => (
		  <OptionsColumn position="leftEnd">
			  <RCCDropDownItem 
				selfEnclose
				onClick={() => refModals.current?.actionButtonMaintenance(row.efa005Id, row.efa005Nome)}
			>
				<RCCIcon name="settings" 
				/>
				{translate("Manutenção")}
			</RCCDropDownItem>
			<RCCDropDownItem 
				selfEnclose
				onClick={() => refModalsProfile.current?.openModalResetPassword(row.efa005Id)}
			>
				<RCCIcon name="key" 
				/>
				{translate("Alterar senha")}
			</RCCDropDownItem>
			{row.efa005Situacao && row.efa005Situacao.toString() === "1" ? (
				<RCCDropDownItem 
					selfEnclose
					onClick={() => refModals.current?.actionButtonDisable(row.efa005Id, row.efa005Nome)}
				>
					<RCCIcon name="close" 
					/>
					{translate("Desabilitar usuário")}
				</RCCDropDownItem>
			): (
				<RCCDropDownItem 
					selfEnclose
					onClick={() => refModals.current?.actionButtonRestore(row.efa005Id, row.efa005Nome)}
				>
					<RCCIcon name="building" 
					/>
					{translate("Habilitar usuário")}
				</RCCDropDownItem>
			)}
			<RCCDropDownItem
				selfEnclose
				onClick={() => { 
					refModalChangeResponsibleOrAuthority.current?.openModal(row.efa005Id, row.efa005Nome);
				}}
			>
				<RCCIcon name="exchange" />
				{translate("Alterar Responsável/Autoridade")}
			</RCCDropDownItem>
			<RCCDropDownItem
				selfEnclose
				onClick={() => { 
					refModalIndicatorsPermissions.current?.openModal({
						idEfa005: row.efa005Id
					}, `${translate("Usuário")}: ${row.efa005Nome}`);
				}}
			>
				<RCCIcon name="lock" />
				{translate("Permissões de indicadores")}
			</RCCDropDownItem>
			<RCCDropDownItem 
				selfEnclose
				onClick={() => refModals.current?.actionButtonDelete(row.efa005Id, row.efa005Nome)}
			>
				<RCCIcon name="trash" 
				/>
				{translate("Deletar")}
			</RCCDropDownItem>
		  </OptionsColumn>
	);
	
	const colSituacao = (row: any) => (
		<RCCBadge color={row.efa005Situacao && row.efa005Situacao.toString() === "1" ? "green" : "red"}
			styleInline="font-size: 0.83rem;"
		>
			{row.efa005Situacao && row.efa005Situacao.toString() === "1" ? translate("Habilitado") : translate("Desabilitado")}
		</RCCBadge>
	);

	const colUser = (row: any) => <RCCGrid container item alignItems="center">
		<Photo 
			width={28}
			height={28}
			hover={false}
			name={row.efa005Nome||""}
			link={row.efa005Imagem && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${row.efa005Imagem}`}
		/> 
		<DivFlexDirectionColumn>
			<span>{row.efa005Nome}</span>
			<small>{row.efa005Email}</small>
		</DivFlexDirectionColumn>
	</RCCGrid>;

	const colDate = (row: any) => formatDate(row.dateCreated, currentLanguage);

	// List & Pagination invite
	const paginationRefInvite = useRef<IForwardPagination>(null);
	const { confirm } = useConfirmModal();
	const deleteInvite = (id: number, email: string) => {
		async function deleteMethod(){
			await RCCServicesApi.deleteRequest({
				method: "User/DeleteInvite",
				urlParameters: {
					id
				}
			});
		}
		
		confirm(
			translate("Exclusão de convite"), 
			`${translate("Deseja realmente excluir o convite para o email")}: ${email}?`,
			async () => {
				await deleteMethod();
				addToast({
					color: "green",
					description: translate("Convite removido com sucesso"),
					title: translate("Sucesso")
				});

				paginationRefInvite.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao remover convite do dashboard"), addToast);
			},
			true
		);
	};
	const colOptionsInvite = (row: any) => (
		<OptionsColumn>
			<RCCDropDownItem 
			  selfEnclose
			  onClick={() => {
					addToast({
						color: "blue",
						description: translate("Texto copiado"),
						title: translate("Sucesso")
					});
					navigator.clipboard.writeText(`${window.location.origin}/users/invite/${row.efa026Hash}`);
			  }}
		  >
			  <RCCIcon name="copy" 
			  />
			  {translate("Copiar")} link
		  </RCCDropDownItem>
		  <RCCDropDownItem 
			  selfEnclose
			  onClick={() => deleteInvite(row.efa026Id, row.efa026Email)}
		  >
			  <RCCIcon name="trash" 
			  />
			  {translate("Deletar")}
		  </RCCDropDownItem>
		</OptionsColumn>
	);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Usuários")}
					icon={() => <AppIcons name="configs" />}
				>
					<RCCTooltip
						text={translate("Novo usuário")}
						color="purple"
					>
						<RCCButton
							icon={{name: "plus", color: "#FFF", size: 18}}
							color="purple"
							layout="isRounded"
							onClick={() => refModals.current?.actionButtonRegister(businessGroup.efa001Id)}
							size="md"
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Convidar usuários")}
						color="purple"
					>
						<RCCButton
							icon={{name: "users", color: "#FFF", size: 18}}
							color="purple"
							layout="isRounded"
							onClick={() => refModals.current?.actionButtonInvite()}
							size="md"
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Importar usuários")}
						color="grey"
					>
						<RCCButton
							icon={{name: "upload", color: "#FFF", size: 15}}
							color="grey"
							layout="isRounded"
							onClick={() => refModalImportUsers.current?.actionButtonImportUsers(businessGroup.efa001Id)}
							size="md"
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || filters.f || filters.enabledUsers ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || filters.f || filters.enabledUsers ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>
					
				{/* Filters */}
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({
						f: "",
						enabledUsers: true
					})}
					showFilters={showFilters}
				>
					<RCCGrid container item alignItems="flex-end">
						<DivFlexDirectionColumn style={{ flex: "none" }}>
							<RCCSwitch 
								name="enabledUsers" 
								label={translate("Mostrar apenas habilitados")} 
								register={registerFilters} 
								defaultChecked={filters.enabledUsers}
								labelPosition="end"
							/>
						</DivFlexDirectionColumn>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="f" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={filters.f}
							/>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>

				<Accordion 
					header={translate("Convites")}
					unmountOnExit={false}
				>
					<Pagination 
						ref={paginationRefInvite}
						dataOrRoute="User/ListInvite"
						columns={[
							{
								idColumn: "efa026Email",
								nameColumn: "Email",
								xs: 12,
								sm: 6,
								md: 6
							},		
							{
								idColumn: "efa026Efa004Nome",
								nameColumn: translate("Grupo de usuário"),
								xs: 12,
								sm: 3,
								md: 3
							},
							{
								idColumn: "dateCreated",
								nameColumn: translate("Criado em"),
								xs: 12,
								sm: 2,
								md: 2,
								cellRenderer: colDate
							},
							{
								idColumn: "options",
								nameColumn: " ",
								justify: "right",
								xs: 12,
								sm: 1,
								md: 1,
								cellRenderer: colOptionsInvite,
								disableSort: true
							}
						]}
					/>
				</Accordion>

				{/* List */}
				<Pagination 
					ref={paginationRef}
					dataOrRoute="User/List"
					filtersData={filters}     
					columns={[
	  					{
							idColumn: "efa005Nome",
							nameColumn: translate("Usuário"),
							xs: 12,
							sm: 6,
							md: 6,
							cellRenderer: colUser
	  					},
	  								
						{
							idColumn: "dateCreated",
							nameColumn: translate("Criado em"),
							xs: 12,
							sm: 3,
							md: 3,
							cellRenderer: colDate
	  					},
						{
							idColumn: "efa005Situacao",
							nameColumn: translate("Situação"),
							xs: 12,
							sm: 2,
							md: 2,
							cellRenderer: colSituacao
	  					},
						{
							idColumn: "options",
							nameColumn: " ",
							justify: "right",
							xs: 12,
							sm: 1,
							md: 1,
							cellRenderer: colOptions,
							disableSort: true
						}
					]}
				/>
							
			</RCCGrid>

			<ModalsUsers 
				ref={refModals}
				paginationRef={paginationRef}
				paginationInvitationRef={paginationRefInvite}
			/>

			<ModalsProfile 
				ref={refModalsProfile}
			/>

			<ModalImportUsers 
				ref={refModalImportUsers}
				paginationRef={paginationRef}
			/>

			<ModalIndicatorsPermissions 
				ref={refModalIndicatorsPermissions}
			/>

			<ModalChangeResponsibleOrAuthority 
				ref={refModalChangeResponsibleOrAuthority}
			/>

		</>
	);
};

export default UsersPage;
